/**
 * Canonical resume ITEM SCHEMA — the browser mirror of
 * infrastructure/lambdas/shared/resume_template.py.
 *
 * The field table is NOT invented here. It is a hand-mirrored copy of
 * `tests/resume_item_schema.json`, which pytest GENERATES from the live Python
 * schemas. `webapp/src/__tests__/resumeSchema.test.ts` asserts this file equals
 * that JSON byte-for-byte in structure, and
 * `infrastructure/tests/test_resume_item_schema.py` asserts the JSON equals the
 * Python. One schema, two runtimes, one source of truth — a field added on one
 * side and forgotten on the other turns a build red instead of silently eating
 * user data.
 *
 * WHY THIS EXISTS: before it, five sites carried their own hand-typed idea of
 * "what a resume item looks like" and they drifted. The editor's normalizer
 * rebuilt every experience bullet as `{text, tags}`, so an UNCHECKED bullet
 * (`include: '0'`) came back CHECKED on the next load — the flag was thrown away
 * client-side while the server faithfully stored it. The YAML export dropped it
 * too. Both now derive their field lists from this mirror.
 *
 * TO ADD A FIELD: edit the Python `*_ITEM_SCHEMA`, run
 *   python3 infrastructure/tests/test_resume_item_schema.py --regen
 * then mirror the same entry here. Nothing else in the webapp should ever
 * re-type a resume item's field names.
 */

export type SchemaFieldType = 'str' | 'list' | 'bool' | 'float' | 'dict';

export interface SchemaField {
  type: SchemaFieldType;
  required: boolean;
  default?: string;
  item_schema?: ItemSchema;
}

export type ItemSchema = Record<string, SchemaField>;

export interface SectionSchema {
  type: 'list' | 'str' | 'dict';
  required: boolean;
  item_type?: string;
  item_schema?: ItemSchema;
}

const CONTACT_ITEM_SCHEMA: ItemSchema = {
  icon: { type: 'str', required: true },
  title: { type: 'str', required: true },
  url: { type: 'str', required: false },
  include: { type: 'str', required: false, default: '1' },
};

const SKILL_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
  group_id: { type: 'str', required: false },
};

const EXPERIENCE_BULLET_SCHEMA: ItemSchema = {
  text: { type: 'str', required: true },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

const EXPERIENCE_ROLE_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  startDate: { type: 'str', required: false },
  startDate_iso: { type: 'str', required: false },
  endDate: { type: 'str', required: false },
  endDate_iso: { type: 'str', required: false },
  current: { type: 'bool', required: false },
  location: { type: 'str', required: false },
  bullets: { type: 'list', required: false, item_schema: EXPERIENCE_BULLET_SCHEMA },
  include: { type: 'str', required: false, default: '1' },
};

const EXPERIENCE_ITEM_SCHEMA: ItemSchema = {
  company: { type: 'str', required: true },
  url: { type: 'list', required: false },
  employees: { type: 'str', required: false },
  location: { type: 'str', required: false },
  description: { type: 'str', required: false },
  startDate: { type: 'str', required: false },
  startDate_iso: { type: 'str', required: false },
  endDate: { type: 'str', required: false },
  endDate_iso: { type: 'str', required: false },
  current: { type: 'bool', required: false },
  roles: { type: 'list', required: false, item_schema: EXPERIENCE_ROLE_SCHEMA },
  include: { type: 'str', required: false, default: '1' },
};

const EDUCATION_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  org: { type: 'str', required: true },
  url: { type: 'str', required: false },
  year: { type: 'str', required: false },
  year_iso: { type: 'str', required: false },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

const AWARD_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  issuer: { type: 'str', required: false },
  reward: { type: 'str', required: false },
  url: { type: 'str', required: false },
  year: { type: 'str', required: false },
  year_iso: { type: 'str', required: false },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

const PRESENTATION_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  event: { type: 'str', required: false },
  url: { type: 'str', required: false },
  year: { type: 'str', required: false },
  year_iso: { type: 'str', required: false },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

const PUBLICATION_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  url: { type: 'str', required: false },
  year: { type: 'str', required: false },
  year_iso: { type: 'str', required: false },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

const SUMMARY_PHRASE_ITEM_SCHEMA: ItemSchema = {
  text: { type: 'str', required: true },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
};

const PORTFOLIO_ITEM_SCHEMA: ItemSchema = {
  title: { type: 'str', required: true },
  url: { type: 'str', required: false },
  year: { type: 'str', required: false },
  year_iso: { type: 'str', required: false },
  tags: { type: 'list', required: false },
  include: { type: 'str', required: false, default: '1' },
  match_score: { type: 'float', required: false },
};

/** Mirror of shared/resume_template.SECTION_SCHEMAS (structure only). */
export const SECTION_SCHEMAS: Record<string, SectionSchema> = {
  contact: { type: 'list', required: true, item_schema: CONTACT_ITEM_SCHEMA },
  summary: { type: 'str', required: true },
  skills: { type: 'list', required: true, item_schema: SKILL_ITEM_SCHEMA },
  highlights: { type: 'list', required: true, item_type: 'str' },
  experience: { type: 'list', required: true, item_schema: EXPERIENCE_ITEM_SCHEMA },
  education: { type: 'list', required: true, item_schema: EDUCATION_ITEM_SCHEMA },
  awards: { type: 'list', required: true, item_schema: AWARD_ITEM_SCHEMA },
  presentations: { type: 'list', required: false, item_schema: PRESENTATION_ITEM_SCHEMA },
  publications: { type: 'list', required: false, item_schema: PUBLICATION_ITEM_SCHEMA },
  portfolios: { type: 'list', required: false, item_schema: PORTFOLIO_ITEM_SCHEMA },
  summary_phrases: { type: 'list', required: false, item_schema: SUMMARY_PHRASE_ITEM_SCHEMA },
};

/** Mirror of shared/resume_template.RESUME_TOP_LEVEL. */
export const RESUME_TOP_LEVEL: Record<string, { type: string; required: boolean }> = {
  name: { type: 'str', required: true },
};

/**
 * Resolve a dotted path ("experience.roles.bullets") to its item schema.
 * Mirrors `resume_template.get_item_schema`.
 */
export function getItemSchema(path: string): ItemSchema | null {
  const parts = (path || '').split('.').filter(Boolean);
  if (!parts.length) return null;
  const section = SECTION_SCHEMAS[parts[0]];
  if (!section) return null;
  let schema: ItemSchema | undefined = section.item_schema;
  for (const part of parts.slice(1)) {
    if (!schema) return null;
    schema = schema[part]?.item_schema;
  }
  return schema || null;
}

/** Legal field names, in schema order, for a dotted path. */
export function schemaFieldNames(path: string): string[] {
  const schema = getItemSchema(path);
  return schema ? Object.keys(schema) : [];
}

function coerceList(value: any): any[] {
  if (Array.isArray(value)) return value;
  // A lone scalar becomes a one-element list rather than being thrown away —
  // same rule as the Python coercion in post_processors/formatting._coerce_item,
  // and what already kept a legacy string `url` alive on an experience entry.
  return value ? [value] : [];
}

/**
 * Rebuild `item` keeping ONLY schema-known fields (allowlist = the schema).
 *
 * Used where the editor previously hand-rebuilt an item and, in doing so, ate
 * fields it had never been taught about. Unknown/junk keys are still dropped —
 * the POLICY is unchanged, only its source. List fields are always emitted (as
 * arrays) and required strings are always emitted (defaulting to ''), matching
 * exactly what the old hand-written rebuild guaranteed; everything else is
 * emitted only when the item actually carries it, so absent optional fields are
 * not invented.
 */
export function pickSchemaFields<T = any>(item: any, path: string): T {
  const schema = getItemSchema(path);
  if (!schema || !item || typeof item !== 'object') return item as T;
  const out: Record<string, any> = {};
  for (const [field, def] of Object.entries(schema)) {
    const value = (item as any)[field];
    if (def.type === 'list') {
      out[field] = def.item_schema
        ? (Array.isArray(value) ? value : [])
        : coerceList(value);
      continue;
    }
    if (def.required && def.type === 'str') {
      out[field] = value || '';
      continue;
    }
    if (value === undefined || value === null) continue;
    out[field] = value;
  }
  return out as T;
}

/**
 * Guarantee an item's list fields are arrays, IN PLACE, leaving every other key
 * untouched.
 *
 * This is the non-destructive counterpart to `pickSchemaFields`, for the
 * sections the editor has always normalized in place (education, awards,
 * presentations, publications, portfolios, skills, summary phrases, experience
 * companies/roles). Those sites never dropped unknown keys and must not start
 * doing so now — this only fills in the array shape the UI iterates over, with
 * the field list coming from the schema instead of a hand-typed list of
 * "…ensure X has tags" lines.
 */
export function ensureSchemaLists(item: any, path: string): any {
  const schema = getItemSchema(path);
  if (!schema || !item || typeof item !== 'object') return item;
  for (const [field, def] of Object.entries(schema)) {
    if (def.type !== 'list') continue;
    if (def.item_schema) {
      // Structural child list (roles, bullets): guarantee an array, never wrap.
      if (!Array.isArray(item[field])) item[field] = [];
    } else if (!Array.isArray(item[field])) {
      item[field] = coerceList(item[field]);
    }
  }
  return item;
}

/**
 * Project an item for YAML export: every schema field the item actually
 * carries, in schema order.
 *
 * Absent fields (undefined/null) are skipped so the export never invents data,
 * but a field the record HAS is always emitted — including `include`, which the
 * old hand-written export dropped, breaking the export -> edit -> re-import
 * round-trip for every unchecked item.
 */
export function exportSchemaFields(item: any, path: string): Record<string, any> {
  const schema = getItemSchema(path);
  if (!schema || !item || typeof item !== 'object') return item;
  const out: Record<string, any> = {};
  for (const field of Object.keys(schema)) {
    const value = (item as any)[field];
    if (value === undefined || value === null) continue;
    out[field] = value;
  }
  return out;
}
